import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "../api";
import { useAuth } from "../auth";
import { useLanguage } from "../i18n";

// etichette dei passaggi riusano le chiavi esistenti del flusso upload
const STEP_KEYS = {
  document_front: "id.docFront", document_back: "id.docBack",
  selfie_front: "id.selfieFront", selfie_right: "id.selfieRight", selfie_left: "id.selfieLeft",
  selfie_video: "id.selfieVideo",
};

/** Alert globale identità: impossibile non vederlo dopo il login. Appare su ogni
 * pagina quando l'ultima pratica è "da rifare" (documenti rifiutati) o "scaduta".
 * Il CTA porta dritto al flusso di re-upload via link token (nessun login necessario),
 * altrimenti alla card identità nell'area account. */
export default function IdentityRedoAlert() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!user || user.is_admin) { setInfo(null); return undefined; }
    let alive = true;
    api.get("/identity/status").then(({ data }) => { if (alive) setInfo(data); }).catch(() => {});
    return () => { alive = false; };
  }, [user, location.pathname]);

  const st = info?.data?.status;
  if (!user || user.is_admin) return null;
  if (st !== "rejected" && st !== "expired") return null;
  if (location.pathname.startsWith("/identity-upload")) return null;  // già nella pagina giusta

  const redo = (info?.data?.redo_kinds || []).map((k) => t(STEP_KEYS[k] || k));
  // path relativo: funziona sia in anteprima sia in produzione (il token è per-sessione)
  const target = info?.upload_token ? `/identity-upload/${info.upload_token}` : "/account?tab=account&focus=identity";
  return (
    <div data-testid="identity-redo-alert" className={st === "expired" ? "bg-slate-900 text-white" : "bg-rose-600 text-white"}>
      <div className="max-w-7xl mx-auto px-3 sm:px-8 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <p className="flex-1 min-w-0">
          <span className="block truncate text-xs sm:text-sm font-bold">
            {st === "expired" ? t("id.alertExpired") : t("id.alertRedo")}
          </span>
          {redo.length > 0 && (
            <span data-testid="identity-redo-alert-kinds" className={`block truncate text-[11px] sm:text-xs ${st === "expired" ? "text-slate-300" : "text-rose-100"}`}>
              {redo.join(" · ")}
            </span>
          )}
        </p>
        <Link to={target} data-testid="identity-redo-alert-cta"
          className={`shrink-0 rounded-full bg-white px-4 min-h-[36px] inline-flex items-center font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
            st === "expired" ? "text-slate-900 hover:bg-slate-100" : "text-rose-700 hover:bg-rose-50"}`}>
          {st === "expired" ? t("id.restartBtn") : t("id.alertCta")}
        </Link>
      </div>
    </div>
  );
}
